"use client";

import { useState, useEffect } from "react";
import PartnerHotelsClient from "@/components/voucher/PartnerHotelsClient";
import HotelLocationMap from "@/components/deal/HotelLocationMap";
import HotelImpressionsCarousel from "@/components/deal/HotelImpressionsCarousel";
import { KerstJouwHotels } from "@/components/deal/KerstDealBlokken";
import { orderHotels } from "@/components/deal/orderHotels";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";
import { haalDealHotels } from "@/lib/deal/deal-hotels-verzoek";

/**
 * Fetches hotel details for a deal and renders the PartnerHotelsClient modal component.
 * Used on deal detail pages to make hotel names clickable with detail popups.
 */
export default function DealPartnerHotels({ dealId, hotelIds = [], hotelTitle, impressionPhotos = [], showImpressions = false, impressionLeadHotelId = null, impressionOrder = null, kerstHotels = null, dealCity = null, initialHotels = null }) {
  const { locale } = useSiteContext();
  const { t } = getTranslations(locale);
  // De server haalt de hotels op en geeft ze mee, zodat ze in de HTML staan
  // (crawlers voeren de browser-fetch niet uit). Zonder (of bij een mislukte
  // serveraanroep) haalt de component ze zoals voorheen in de browser op.
  const hasInitial = Array.isArray(initialHotels) && initialHotels.length > 0;
  const [hotels, setHotels] = useState(hasInitial ? initialHotels : []);
  const [loading, setLoading] = useState(!hasInitial);
  // De state volgt op dealId en initialHotels (hergebruik van de component met
  // een andere deal): bij een wijziging tijdens het renderen opnieuw beginnen.
  // Een nieuwe lege/null-waarde zonder eerdere serverhotels telt niet als wijziging.
  const [vorige, setVorige] = useState({ dealId, hasInitial, initialHotels });
  if (
    vorige.dealId !== dealId ||
    vorige.hasInitial !== hasInitial ||
    (hasInitial && vorige.initialHotels !== initialHotels)
  ) {
    setVorige({ dealId, hasInitial, initialHotels });
    setHotels(hasInitial ? initialHotels : []);
    setLoading(!hasInitial);
  }

  useEffect(() => {
    if (!dealId || hasInitial) return;
    let vervallen = false;
    // Gedeeld met de hotelregel onder de boekknop: samen één verzoek.
    haalDealHotels(dealId)
      .then((data) => {
        if (!vervallen) setHotels(data);
      })
      .finally(() => {
        if (!vervallen) setLoading(false);
      });
    return () => {
      vervallen = true;
    };
  }, [dealId, hasInitial]);

  if (loading) {
    return (
      <div className="py-4 text-center text-gray-400 text-sm">{t("booking.hotels.loading")}</div>
    );
  }

  if (!hotels.length) return null;

  const matched =
    hotelIds.length > 0 ? hotels.filter((h) => hotelIds.includes(h.hotel_id)) : hotels;

  // List and map follow the same per-deal order as the carousel (weak hotels
  // last); without an order this is the unchanged backend list.
  const orderedHotels = orderHotels(hotels, impressionOrder);

  return (
    <>
      {/* "Sfeerbeeld van de hotels" carousel: kept ONLY on the ING deal where it
          was conceived. Dennis 2026-06-03: the impression/auto shots look ugly on
          regular offers, so remove them there. Requires curated photos (no ugly
          auto-fallback). */}
      {showImpressions && (
        <HotelImpressionsCarousel hotels={matched} photos={impressionPhotos} leadHotelId={impressionLeadHotelId} order={impressionOrder} />
      )}
      <PartnerHotelsClient
        hotels={orderedHotels}
        hotelIds={hotelIds}
        hotelTitle={hotelTitle}
        leadHotelId={impressionLeadHotelId}
        dealCity={dealCity}
      />
      {Array.isArray(kerstHotels) && kerstHotels.length > 0 && (
        <KerstJouwHotels hotels={orderHotels(matched, impressionOrder)} kerstHotels={kerstHotels} />
      )}
      <HotelLocationMap hotels={orderedHotels} dealCity={dealCity} locale={locale} />
    </>
  );
}
